import { useEffect, useRef, useState } from 'react';

export default function WritingFocus() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const focuses = [
    {
      icon: 'ri-quill-pen-line',
      title: 'Storytelling for Impact',
      description: 'Crafting meaningful narratives that evoke emotion, build connection, and drive action.',
      gradient: 'from-purple-500 to-purple-600'
    },
    {
      icon: 'ri-edit-line',
      title: 'Content Writing with Blogger',
      description: 'Blending creativity with communication goals; writing that aligns with brand voice and audience needs.',
      gradient: 'from-rose-500 to-rose-600'
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 px-6 bg-gradient-to-br from-purple-50/50 via-white to-rose-50/50">
      <div className="container mx-auto max-w-6xl">
        <div className={`transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-rose-500 bg-clip-text text-transparent" style={{ fontFamily: '"Playfair Display", serif' }}>
              Writing Focuses
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" style={{ fontFamily: '"Lora", serif' }}>
              My approach to creating content that resonates and inspires
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-500 to-rose-500 mx-auto rounded-full mt-6"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {focuses.map((focus, index) => (
              <div
                key={index}
                className={`group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${200 + index * 150}ms` }}
              >
                <div className={`w-20 h-20 flex items-center justify-center rounded-2xl bg-gradient-to-br ${focus.gradient} mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                  <i className={`${focus.icon} text-4xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4" style={{ fontFamily: '"Playfair Display", serif' }}>
                  {focus.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" style={{ fontFamily: '"Lora", serif' }}>
                  {focus.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
